import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const fmt = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 2,
});

const pct = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function VarietyAnalysisDetail() {
  const navigate = useNavigate();
  const query = useQuery();

  const fromDate = query.get("from") || "";
  const toDate = query.get("to") || "";
  const initialVariety = query.get("variety") || "";

  const [summaryRows, setSummaryRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [summaryMeta, setSummaryMeta] = useState({});
  const [detailRows, setDetailRows] = useState([]);
  const [detailSummary, setDetailSummary] = useState({});
  const [selectedVariety, setSelectedVariety] = useState(initialVariety);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadSummary() {
      if (!fromDate || !toDate) {
        setError("Select both From Date and To Date in the CEO Command Center.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const res = await api.post(
          "/ai/ceo-chat/variety",
          {
            from_date: fromDate,
            to_date: toDate,
          },
          { timeout: 120000 }
        );

        if (!active) return;

        const data = res.data || {};
        setSummaryRows(data.rows || []);
        setSummary(data.summary || {});
        setSummaryMeta(data || {});
      } catch (e) {
        if (!active) return;
        setError(
          e?.response?.data?.detail ||
            e?.message ||
            "Variety analysis could not be loaded."
        );
      } finally {
        if (active) setLoading(false);
      }
    }

    loadSummary();

    return () => {
      active = false;
    };
  }, [fromDate, toDate]);

  useEffect(() => {
    let active = true;

    async function loadDetail() {
      if (!fromDate || !toDate) return;

      setDetailLoading(true);
      setError("");

      try {
        const res = await api.post(
          "/ai/ceo-chat/variety/detail",
          {
            from_date: fromDate,
            to_date: toDate,
            variety: selectedVariety || "",
          },
          { timeout: 120000 }
        );

        if (!active) return;

        const data = res.data || {};
        setDetailRows(data.rows || []);
        setDetailSummary(data.summary || {});
        setPage(1);
      } catch (e) {
        if (!active) return;
        setError(
          e?.response?.data?.detail ||
            e?.message ||
            "Variety drill-down could not be loaded."
        );
      } finally {
        if (active) setDetailLoading(false);
      }
    }

    loadDetail();

    return () => {
      active = false;
    };
  }, [fromDate, toDate, selectedVariety]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return detailRows;

    return detailRows.filter((row) =>
      Object.values(row || {}).some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(q)
      )
    );
  }, [detailRows, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRows.length / pageSize)
  );

  const safePage = Math.min(page, totalPages);

  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, safePage, pageSize]);

  const exportCsv = () => {
    const cols = [
      ["InvoiceNumber", "Invoice Number"],
      ["InvoiceDate", "Invoice Date"],
      ["PoNumber", "PO Number"],
      ["PoDate", "PO Date"],
      ["CustomerName", "Customer / Consignee"],
      ["DestinationCountry", "Country"],
      ["Variety", "Variety"],
      ["VarietyName", "Variety Name"],
      ["Grade", "Grade"],
      ["PackingStyle", "Packing Style"],
      ["BrandName", "Brand"],
      ["MCS", "MCS"],
      ["ProcessorName", "Processor / Plant"],
      ["SourceVarietyDescription", "ERP Source Description"],
    ];

    const esc = (v) => {
      const s = String(v ?? "");
      if (/[",\n]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const lines = [
      cols.map(([, label]) => esc(label)).join(","),
      ...filteredRows.map((row) =>
        cols.map(([key]) => esc(row?.[key])).join(",")
      ),
    ];

    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `variety-analysis-${fromDate}-${toDate}${
      selectedVariety ? `-${selectedVariety}` : ""
    }.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const cards = [
    {
      label: "Distinct Varieties",
      value: fmt.format(summary.distinct_varieties || 0),
      sub: "ERP Variety master ShortName",
    },
    {
      label: "Total Commercial MCS",
      value: fmt.format(summary.total_mcs || 0),
      sub: "ScrmCIGradeData.Mcs",
    },
    {
      label: "POs",
      value: fmt.format(summary.pos || 0),
      sub: "Distinct PO count",
    },
    {
      label: "Grade Lines",
      value: fmt.format(summary.grade_lines || 0),
      sub: "Validated invoice grade-line grain",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1700px] space-y-5">
        <div className="flex flex-col gap-3 rounded-2xl border bg-white p-5 shadow-sm md:flex-row md:items-start md:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              R23 · CEO Variety Analysis
            </div>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">
              Commercial Variety Mix
            </h1>
            <p className="mt-2 max-w-4xl text-sm text-slate-600">
              Variety-wise commercial quantity using ERP Variety master
              ShortName and ScrmCIGradeData MCS. USD/value measures are
              intentionally excluded until commercial FX/value validation is
              approved.
            </p>
          </div>

          <button
            type="button"
            onClick={() =>
              navigate("/ceo-command-center?tab=commercial")
            }
            className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Back to Commercial
          </button>
        </div>

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {cards.map((card) => (
            <div
              key={card.label}
              className="rounded-2xl border bg-white p-5 shadow-sm"
            >
              <div className="text-sm font-medium text-slate-500">
                {card.label}
              </div>
              <div className="mt-2 text-3xl font-semibold text-slate-900">
                {loading ? "…" : card.value}
              </div>
              <div className="mt-2 text-xs text-slate-500">
                {card.sub}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Variety Mix
              </h2>
              <p className="text-sm text-slate-500">
                {fromDate} to {toDate} · Date basis:
                {" "}
                {summaryMeta?.date_range?.date_field ||
                  "ScrmCustomInvoices.CInvDate"}
              </p>
            </div>

            <div className="text-xs text-slate-500">
              Reconciliation:
              {" "}
              <span
                className={
                  summaryMeta?.reconciliation?.pass
                    ? "font-semibold text-emerald-700"
                    : "font-semibold text-red-700"
                }
              >
                {summaryMeta?.reconciliation?.pass ? "PASS" : "PENDING"}
              </span>
            </div>
          </div>

          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-3">Variety</th>
                  <th className="px-3 py-3">Name</th>
                  <th className="px-3 py-3 text-right">MCS</th>
                  <th className="px-3 py-3 text-right">Share %</th>
                  <th className="px-3 py-3 text-right">POs</th>
                  <th className="px-3 py-3 text-right">Grade Lines</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {summaryRows.map((row) => {
                  const active = selectedVariety === row.Variety;

                  return (
                    <tr
                      key={row.Variety}
                      className={`border-b last:border-0 ${
                        active ? "bg-slate-50" : ""
                      }`}
                    >
                      <td className="px-3 py-3 font-semibold text-slate-900">
                        {row.Variety}
                      </td>
                      <td className="px-3 py-3 text-slate-700">
                        {row.VarietyName || "—"}
                      </td>
                      <td className="px-3 py-3 text-right font-medium">
                        {fmt.format(row.MCS || 0)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {pct.format(row.SharePct || 0)}%
                      </td>
                      <td className="px-3 py-3 text-right">
                        {fmt.format(row.POs || 0)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {fmt.format(row.GradeLines || 0)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedVariety(
                              active ? "" : row.Variety
                            )
                          }
                          className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          {active ? "Show All" : "Drill Down"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl border bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b p-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                {selectedVariety
                  ? `${selectedVariety} Drill-down`
                  : "All Variety Grade-line Detail"}
              </h2>
              <p className="text-sm text-slate-500">
                {fmt.format(detailSummary.grade_lines || 0)} grade lines ·
                {" "}
                {fmt.format(detailSummary.pos || 0)} POs ·
                {" "}
                {fmt.format(detailSummary.total_mcs || 0)} MCS
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search invoice, PO, customer, grade..."
                className="w-72 rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
              />

              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
              >
                {[25, 50, 100, 250, 500].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={exportCsv}
                className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                Export CSV
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1500px] text-sm">
              <thead>
                <tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">Invoice</th>
                  <th className="px-4 py-3">Invoice Date</th>
                  <th className="px-4 py-3">PO</th>
                  <th className="px-4 py-3">PO Date</th>
                  <th className="px-4 py-3">Customer / Consignee</th>
                  <th className="px-4 py-3">Country</th>
                  <th className="px-4 py-3">Variety</th>
                  <th className="px-4 py-3">Grade</th>
                  <th className="px-4 py-3">Packing</th>
                  <th className="px-4 py-3">Brand</th>
                  <th className="px-4 py-3 text-right">MCS</th>
                  <th className="px-4 py-3">Processor / Plant</th>
                </tr>
              </thead>
              <tbody>
                {detailLoading ? (
                  <tr>
                    <td
                      colSpan={12}
                      className="px-4 py-10 text-center text-slate-500"
                    >
                      Loading validated ERP detail…
                    </td>
                  </tr>
                ) : pagedRows.length ? (
                  pagedRows.map((row) => (
                    <tr
                      key={`${row.GradeLineID}-${row.InvoiceID}`}
                      className="border-b last:border-0 hover:bg-slate-50"
                    >
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {row.InvoiceNumber || "—"}
                      </td>
                      <td className="px-4 py-3">
                        {row.InvoiceDate || "—"}
                      </td>
                      <td className="px-4 py-3">
                        {row.PoNumber || "—"}
                      </td>
                      <td className="px-4 py-3">
                        {row.PoDate || "—"}
                      </td>
                      <td className="min-w-[260px] px-4 py-3">
                        {row.CustomerName || "—"}
                      </td>
                      <td className="px-4 py-3">
                        {row.DestinationCountry || "—"}
                      </td>
                      <td className="px-4 py-3 font-semibold">
                        {row.Variety || "—"}
                      </td>
                      <td className="px-4 py-3">
                        {row.Grade || "—"}
                      </td>
                      <td className="min-w-[180px] px-4 py-3">
                        {row.PackingStyle || "—"}
                      </td>
                      <td className="min-w-[180px] px-4 py-3">
                        {row.BrandName || "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        {fmt.format(row.MCS || 0)}
                      </td>
                      <td className="min-w-[180px] px-4 py-3">
                        {row.ProcessorName || "—"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={12}
                      className="px-4 py-10 text-center text-slate-500"
                    >
                      No Variety detail records found for the selected period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-3 border-t p-4 md:flex-row md:items-center md:justify-between">
            <div className="text-xs text-slate-500">
              Showing {pagedRows.length} of {filteredRows.length} filtered
              rows
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={safePage <= 1}
                onClick={() =>
                  setPage((p) => Math.max(1, p - 1))
                }
                className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-40"
              >
                Previous
              </button>

              <span className="text-sm text-slate-600">
                Page {safePage} of {totalPages}
              </span>

              <button
                type="button"
                disabled={safePage >= totalPages}
                onClick={() =>
                  setPage((p) => Math.min(totalPages, p + 1))
                }
                className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border bg-white p-4 text-xs text-slate-500 shadow-sm">
          Source: ScrmCIGradeData → ScrmCustomInvoices →
          ScrmPIVarietyList. Variety grouping =
          ScrmPIVarietyList.ShortName. Quantity =
          ScrmCIGradeData.Mcs. Date basis =
          ScrmCustomInvoices.CInvDate. USD/value intentionally excluded until
          FX/value validation is approved.
        </div>
      </div>
    </div>
  );
}
